import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Alert,
  Spin,
  Typography,
  Divider,
  Row,
  Col,
} from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import type { CustomerData, BacPaymentRequest } from '../types';

const { Title, Text } = Typography;

interface PaymentFormValues {
  identification: string;
  name: string;
  email: string;
  phone: string;
  iban: string;
  amount: number;
  // Campos opcionales que vienen del chatbot — los mantenemos en el state
  // pero no se editan en el form.
}

export function PaymentForm() {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const encryptId = searchParams.get('id');
  const navigate = useNavigate();

  const [form] = Form.useForm<PaymentFormValues>();
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerData, setCustomerData] = useState<CustomerData>({});

  useEffect(() => {
    if (!encryptId) return;
    let cancelled = false;

    const prefill = async () => {
      setLoadingPrefill(true);
      setError(null);
      try {
        const response = await api.getEncrypted(encryptId);
        if (cancelled) return;

        if (response.success && response.body?.decipherData) {
          const parsed: CustomerData = JSON.parse(response.body.decipherData);
          setCustomerData({ ...parsed, encryptId });
          form.setFieldsValue({
            identification: parsed.identification || '',
            name: parsed.name || '',
            email: parsed.email || '',
            phone: parsed.phone || '',
            amount: parsed.monto || 0,
          });
        } else {
          setError('No se pudieron cargar los datos del cliente. Completá el formulario manualmente.');
        }
      } catch (err: any) {
        if (cancelled) return;
        console.error('[PaymentForm] error fetching encrypt data:', err);
        setError('No se pudieron cargar los datos del cliente. Completá el formulario manualmente.');
      } finally {
        if (!cancelled) setLoadingPrefill(false);
      }
    };

    prefill();
    return () => {
      cancelled = true;
    };
  }, [encryptId, form]);

  const handleSubmit = async (values: PaymentFormValues) => {
    if (!orderId) {
      setError('Falta el ID de la orden.');
      return;
    }

    setSubmitting(true);
    setError(null);

    const payload: BacPaymentRequest = {
      amount: values.amount,
      description: `Pago COFASA orden ${orderId}`,
      personalInfo: {
        identification: values.identification,
        name: values.name,
        email: values.email,
        phone: values.phone,
      },
      paymentMethod: {
        iban: values.iban,
        encryptId: customerData.encryptId,
      },
      metadata: {
        numero_cliente: customerData.numero_cliente || '',
        comunicacion_id: customerData.comunicacion_id || '',
        cod_moneda: customerData.cod_moneda || 'CRC',
        // El chatbot puede enviar facturas_ids/creditos_ids como:
        //   - strings:  ["D391754406"]               → lo envolvemos con {documento_id, monto}
        //   - objetos:  [{documento_id, monto, clase_doc, ...}] → lo propagamos tal cual
        // Oracle exige clase_doc no nulo (ORA-01400), así que el chatbot
        // debe enviarlo dentro del objeto.
        debitos: JSON.stringify(
          (customerData.facturas_ids || []).map((item: any) =>
            typeof item === 'string'
              ? { documento_id: item, monto: values.amount }
              : item
          )
        ),
        creditos: JSON.stringify(
          (customerData.creditos_ids || []).map((item: any) =>
            typeof item === 'string'
              ? { documento_id: item, monto: values.amount }
              : item
          )
        ),
      },
    };

    try {
      const response = await api.postBacPayment(payload);

      if (response.success && response.body) {
        // Redirige a la vista de status. CofasaCGP es el nombre del processor.
        navigate(`/transaction/CofasaCGP/status/${response.body.orderId}`);
      } else {
        setError(response.body?.message || 'No se pudo procesar el pago. Intentalo nuevamente.');
      }
    } catch (err: any) {
      console.error('[PaymentForm] error submitting payment:', err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Error de conexión al procesar el pago.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPrefill) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <Spin size="large" tip="Cargando datos del cliente..." />
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: 560,
        margin: '24px auto',
        padding: '0 16px',
      }}
    >
      <Card
        title={
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <Title level={3} style={{ margin: 0 }}>
              Pago COFASA
            </Title>
            <Text type="secondary">Transferencia bancaria BAC</Text>
          </div>
        }
      >
        {error && (
          <Alert
            type="error"
            message={error}
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
        >
          <Divider orientation="left" plain>
            Datos del cliente
          </Divider>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Identificación"
                name="identification"
                rules={[{ required: true, message: 'Requerido' }]}
              >
                <Input placeholder="1-2345-6789" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Nombre completo"
                name="name"
                rules={[{ required: true, message: 'Requerido' }]}
              >
                <Input placeholder="Juan Pérez" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Requerido' },
                  { type: 'email', message: 'Email inválido' },
                ]}
              >
                <Input placeholder="cliente@example.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Teléfono"
                name="phone"
                rules={[{ required: true, message: 'Requerido' }]}
              >
                <Input placeholder="50688889999" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            Datos del pago
          </Divider>

          <Form.Item
            label="IBAN cuenta BAC"
            name="iban"
            rules={[
              { required: true, message: 'Requerido' },
              // Validación de FORMATO únicamente: 22 caracteres exactos con
              // prefijo "CR" + 20 dígitos (formato IBAN Costa Rica). NO valida
              // que la cuenta exista ni esté activa en BAC — eso requeriría
              // una integración bancaria en tiempo real que todavía no está
              // implementada (ver deuda técnica:
              // docs/a2c-client-cofasa/2026_08_14_184701_deuda_tecnica_integracion_bac_real_spec.md).
              { len: 22, message: 'El IBAN debe tener exactamente 22 caracteres' },
              {
                pattern: /^CR\d{20}$/,
                message: 'Formato inválido. Debe iniciar con CR seguido de 20 dígitos',
              },
            ]}
            extra="Número de cuenta IBAN desde el cual se realizará la transferencia. Solo se valida el formato (CR + 20 dígitos); no se verifica en tiempo real contra el banco."
          >
            <Input placeholder="CR05015202001026284066" maxLength={22} />
          </Form.Item>

          <Form.Item
            label="Monto a pagar"
            name="amount"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <InputNumber
              prefix={customerData.cod_moneda === 'USD' ? '$' : '₡'}
              style={{ width: '100%' }}
              min={0}
              precision={2}
              disabled={Boolean(customerData.monto && customerData.monto > 0)}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => Number(value?.replace(/[,\s]/g, '') || 0) as 0}
            />
          </Form.Item>

          <Form.Item style={{ marginTop: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              icon={<LockOutlined />}
              loading={submitting}
            >
              {submitting ? 'Procesando pago...' : 'Pagar'}
            </Button>
          </Form.Item>

          <Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: 12 }}>
            Tus datos están protegidos. El pago se procesa via BAC Credomatic.
          </Text>
        </Form>
      </Card>
    </div>
  );
}
